import React, {
    FC,
    useState,
    useEffect,
    MouseEvent,
    ChangeEvent,
    FocusEvent,
    KeyboardEvent,
    useRef,
    MutableRefObject
} from 'react';
import {
    InputAutoSuggestWithLabelProps,
    Item
} from './InputAutoSuggestWithLabel.types';
import {
    inputStyle,
    styledWrapperStyle,
    listStyle,
    listItemStyle,
    suggestionButtonStyle,
    buttonTextStyle,
    iconButtonStyle,
    valueStyle,
    iconWrapperStyle,
    searchLabelStyle
} from './InputAutoSuggestWithLabel.styles.css';

import HighlightedText from './HighlightedText/HighlightedText';
import { ClearMedium, PinFilledSmall } from '@zoopla-eng/zds/icons';

const CLEAR_ICON_VALUE = 'Clear';

const dict = {
    ArrowUp: 38,
    ArrowDown: 40,
    Escape: 27,
    Enter: 13
} as const;

function checkKeyEvent(evt: KeyboardEvent, code: keyof typeof dict): boolean {
    if (evt.code) {
        return evt.code === code;
    }
    if (evt.keyCode !== 0) {
        return dict[code] ? dict[code] === evt.keyCode : false;
    }
    return false;
}

export const InputAutoSuggestWithLabel: FC<
    React.PropsWithChildren<InputAutoSuggestWithLabelProps>
> = ({
    getData,
    suggestionSelectedAction,
    validInputRegex,
    showError = false,
    inputOnKeyDown,
    inputTextChange,
    onlyDisplayRemoveIconWhenActive = true,
    onClearText,
    label = 'Search area',
    hasExtendedHeight,
    hasBorderRight = true,
    hasDarkPurpleBorder = false,
    ...props
}) => {
    const signal = useRef<AbortSignal>();
    const [suggestions, setSuggestions] = useState<Item[]>([]);
    const [searchText, setSearchText] = useState(props.value || '');
    const [suggestionIdx, setSuggestionIdx] = useState(-1);
    const [isActive, setIsActive] = useState(false);

    const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const rootNode = useRef() as MutableRefObject<HTMLDivElement>;
    const inputNode = useRef<HTMLInputElement>(null);

    useEffect(() => {
        let unmounted = false;
        function onClick(evt: globalThis.MouseEvent) {
            if (
                rootNode.current &&
                rootNode.current.contains(evt.target as Node)
            ) {
                return;
            }

            if (unmounted) return;

            setSuggestions([]);
            setSuggestionIdx(-1);
        }

        if (typeof window !== 'undefined') {
            document.addEventListener('mousedown', onClick);
        }

        return () => {
            unmounted = true;
            if (typeof window !== 'undefined') {
                document.removeEventListener('mousedown', onClick);
            }
        };
    }, []);

    useEffect(() => {
        setSearchText(props.value || '');
    }, [props.value]);

    useEffect(() => {
        const controller = new AbortController();
        signal.current = controller.signal;
        return () => {
            controller.abort();
        };
    }, []);

    const onTextChange = async (evt: ChangeEvent<HTMLInputElement>) => {
        const {
            target: { value }
        } = evt;

        setSearchText(value);
        if (inputTextChange) {
            inputTextChange(value);
        }

        if (validInputRegex && !validInputRegex.test(value)) {
            return setSuggestions([]);
        }

        const data = await getData(value, signal.current);
        if (signal.current?.aborted) return;
        setSuggestions(data || []);
    };

    const onClearButtonClick = () => {
        setSearchText('');
        setSuggestions([]);
        setSuggestionIdx(-1);
        inputNode.current?.focus();
        setIsActive(true);

        if (onClearText) {
            onClearText();
        }
    };

    const onSuggestionClick = (evt: MouseEvent<HTMLButtonElement>) => {
        const {
            currentTarget: {
                value,
                dataset: { id = '' }
            }
        } = evt;

        setSuggestions([]);
        setSearchText(value);

        if (suggestionSelectedAction) {
            suggestionSelectedAction({ id, value });
        }
    };

    const onKeyDown = (evt: KeyboardEvent) => {
        const { length } = suggestions;
        let idx = -1;

        if (suggestionIdx === -1 && checkKeyEvent(evt, 'Enter')) {
            if (inputOnKeyDown) {
                inputOnKeyDown(evt);
                setSuggestions([]);
                setSuggestionIdx(-1);
            }
            return;
        }

        if (checkKeyEvent(evt, 'ArrowUp') || checkKeyEvent(evt, 'ArrowDown')) {
            evt.preventDefault();
        }

        if (checkKeyEvent(evt, 'ArrowUp') && suggestionIdx > 0) {
            idx = suggestionIdx - 1;
        } else if (checkKeyEvent(evt, 'ArrowUp') && suggestionIdx === 0) {
            idx = length - 1;
        } else if (
            checkKeyEvent(evt, 'ArrowDown') &&
            suggestionIdx < length - 1
        ) {
            idx = suggestionIdx + 1;
        } else if (
            checkKeyEvent(evt, 'ArrowDown') &&
            suggestionIdx === length - 1
        ) {
            idx = 0;
        } else if (checkKeyEvent(evt, 'Escape') && suggestions.length !== 0) {
            setSearchText('');
            setSuggestions([]);
            setSuggestionIdx(-1);
        } else if (checkKeyEvent(evt, 'Enter')) {
            if (suggestions.length) {
                evt.stopPropagation();
                setSuggestions([]);
                const currentEl = itemRefs.current[
                    suggestionIdx === -1 ? 0 : suggestionIdx
                ] as HTMLButtonElement;
                const currentValue = currentEl.value;
                const currentId = currentEl.dataset['id'] as string;

                setSearchText(currentValue);

                if (suggestionSelectedAction) {
                    suggestionSelectedAction({
                        id: currentId,
                        value: currentValue
                    });
                }
            } else {
                if (suggestionSelectedAction) {
                    suggestionSelectedAction({
                        id: '',
                        value: searchText as string
                    });
                }
            }
        }

        setSuggestionIdx(idx);
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
        const { relatedTarget } = event;
        const target = relatedTarget as HTMLInputElement;

        const hasClickedClearButton =
            target && target.value === CLEAR_ICON_VALUE;

        if (hasClickedClearButton) {
            return;
        } else {
            setIsActive(false);
        }
    };

    const shouldDisplayRemoveIcon = onlyDisplayRemoveIconWhenActive
        ? searchText !== '' && isActive
        : searchText !== '';

    return (
        <div
            className={styledWrapperStyle}
            onKeyDown={onKeyDown}
            ref={rootNode}
        >
            <input
                {...props}
                className={inputStyle({
                    hasSuggestions: suggestions.length > 0,
                    hasExtendedHeight,
                    hasBorderRight,
                    hasDarkPurpleBorder
                })}
                type="text"
                onChange={onTextChange}
                value={searchText}
                data-testid="autosuggest-input"
                aria-label="Location"
                ref={inputNode}
                onFocus={() => setIsActive(true)}
                onBlur={handleBlur}
            />
            <p
                className={searchLabelStyle({
                    hasExtendedHeight,
                    showError
                })}
            >
                {showError ? `Please enter a valid search area` : `${label}`}
            </p>
            {shouldDisplayRemoveIcon ? (
                <button
                    className={iconButtonStyle({
                        hasExtendedHeight
                    })}
                    type="button"
                    value={CLEAR_ICON_VALUE}
                    onClick={onClearButtonClick}
                    data-testid="autosuggest-clear"
                    aria-label={CLEAR_ICON_VALUE}
                >
                    <ClearMedium />
                </button>
            ) : null}
            {suggestions.length > 0 && (
                <ul className={listStyle} data-testid="autosuggest-list">
                    {suggestions.map((item, idx) => (
                        <li className={listItemStyle} key={item.id}>
                            <button
                                className={suggestionButtonStyle({
                                    isFocused: idx === suggestionIdx
                                })}
                                data-testid={item.text}
                                type="button"
                                value={item.text}
                                data-id={item.id}
                                onClick={onSuggestionClick}
                                ref={(ref) => {
                                    itemRefs.current[idx] = ref;
                                }}
                            >
                                <div className={iconWrapperStyle}>
                                    <PinFilledSmall color="currentColor" />
                                </div>
                                <span className={buttonTextStyle}>
                                    {item.subValue ? (
                                        <span
                                            className={valueStyle}
                                            data-testid="styled-value"
                                        >
                                            <HighlightedText
                                                text={item.text}
                                                highlight={searchText as string}
                                            />
                                        </span>
                                    ) : (
                                        <HighlightedText
                                            text={item.text}
                                            highlight={searchText as string}
                                        />
                                    )}
                                    {item.subValue}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

InputAutoSuggestWithLabel.displayName = 'InputAutoSuggestWithLabel';
